import React, { useEffect } from "react";
import { Card } from "../common";
import {
  useAppDispatch,
  useAppSelector,
  getActiveAnnouncementsThunk,
} from "../../redux";
import micIcon from "../../assets/icons/mic-purple.svg";
import calendarIcon from "../../assets/icons/calendar.svg";
import fileIcon from "../../assets/icons/file.svg";
import { formatDateDMY } from "../../utils";
import "./AnnouncementCard.css";

interface AnnouncementCardProps {
  title?: string;
  className?: string;
  headerAction?: React.ReactNode;
}

const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  title = "Announcements",
  className = "",
  headerAction,
}) => {
  const dispatch = useAppDispatch();
  const { activeRecords, isLoading, error } = useAppSelector(
    (state) => state.announcement,
  );

  useEffect(() => {
    dispatch(getActiveAnnouncementsThunk());
  }, [dispatch]);

  const formatDateRange = (
    startDate: string | null,
    endDate: string | null,
  ) => {
    if (!startDate && !endDate) return null;
    if (startDate && endDate) {
      if (startDate === endDate) return formatDateDMY(startDate);
      return `${formatDateDMY(startDate)} to ${formatDateDMY(endDate)}`;
    }
    if (startDate) return formatDateDMY(startDate);
    if (endDate) return formatDateDMY(endDate);
    return null;
  };

  const handleViewAttachment = (fileLink: string) => {
    if (!fileLink) return;
    window.open(fileLink, "_blank", "noopener,noreferrer");
  };

  return (
    <Card className={`announcement-card-container ${className}`}>
      <div className="announcement-card-header">
        <div className="announcement-card-title-group">
          <div className="announcement-card-icon-wrapper">
            <img src={micIcon} alt="Announcements" width={20} height={20} />
          </div>
          <h3 className="announcement-card-title">{title}</h3>
          {activeRecords.length > 0 && (
            <span className="announcement-card-badge">
              {activeRecords.length}
            </span>
          )}
        </div>
        {headerAction}
      </div>

      <div className="announcement-card-content">
        {isLoading && activeRecords.length === 0 ? (
          <div className="announcement-card-loading">
            <div className="announcement-card-spinner" />
            <span>Loading announcements...</span>
          </div>
        ) : error && activeRecords.length === 0 ? (
          <div className="announcement-card-empty">
            <p className="announcement-empty-title">
              Unable to load announcements
            </p>
            <p className="announcement-empty-sub">{error}</p>
          </div>
        ) : activeRecords.length === 0 ? (
          <div className="announcement-card-empty">
            <p className="announcement-empty-title">
              No announcements active right now.
            </p>
            <p className="announcement-empty-sub">
              Check back later for updates from your teachers.
            </p>
          </div>
        ) : (
          <div className="announcement-card-list">
            {activeRecords.map((item) => {
              const dateRange = formatDateRange(item.Start_Date, item.End_Date);
              const teacherName = item["Teacher.Name"];

              return (
                <div key={item.id} className="announcement-card-item">
                  <div className="announcement-card-item-top">
                    <h4 className="announcement-card-item-title">
                      {item.Name}
                    </h4>
                    <div className="announcement-card-item-actions">
                      {teacherName && (
                        <span className="announcement-card-teacher-badge">
                          {teacherName}
                        </span>
                      )}
                      {item.File_Link && (
                        <button
                          type="button"
                          className="attachment-action-btn"
                          onClick={() => handleViewAttachment(item.File_Link!)}
                        >
                          <img src={fileIcon} alt="" width={16} height={16} />
                          <span>View File</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {dateRange && (
                    <div className="announcement-card-item-date">
                      <img
                        src={calendarIcon}
                        alt="Date"
                        width={14}
                        height={14}
                      />
                      <span>{dateRange}</span>
                    </div>
                  )}

                  {item.Description && (
                    <p className="announcement-card-item-desc">
                      {item.Description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default AnnouncementCard;
